import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Badge } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { getDashboardMe } from '../../actions/usersActions'

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const toLocal = (utcStr) => new Date(utcStr.replace(' ', 'T') + 'Z')

const formatTime = (d) => d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })

const ClockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
)

const MapPinIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
)

const CalendarSmallIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
)

const BookIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
)

const UserIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
)

const CalendarDayIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626" stroke="none">
        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z" />
    </svg>
)

const VideoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
)

export default function Academic() {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const { dashboard, loading } = useSelector(state => state.users)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        if (!dashboard) dispatch(getDashboardMe())
    }, [dispatch, dashboard])

    const student = dashboard?.students?.[0] || null
    const enrollment = student?.odooEnrollments?.[0] || null
    const allSessions = (student?.odooEnrollments || []).flatMap(e =>
        (e.sessions || []).map(s => ({
            ...s,
            modality: e.modality,
            courseName: e.courseName,
            onlineUrl: s.onlineUrl
        }))
    )

    const now = new Date()
    const dayNames = i18n.language === 'es' ? DAYS : DAYS_EN

    const weekDays = []
    for (let i = 0; i < 7; i++) {
        const d = new Date(now)
        d.setDate(d.getDate() + (i - d.getDay()))
        const daySessions = allSessions.filter(s => {
            const sd = toLocal(s.startDatetime)
            return sd.toDateString() === d.toDateString()
        }).sort((a, b) => toLocal(a.startDatetime) - toLocal(b.startDatetime))
        weekDays.push({
            date: d,
            label: dayNames[d.getDay()],
            isToday: d.toDateString() === now.toDateString(),
            sessions: daySessions
        })
    }

    const [activeTab, setActiveTab] = useState('schedule')

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <h2 className='fw-bold mb-1' style={{ color: '#222', fontSize: '32px' }}>{t('academic.title')}</h2>
                    <p className='text-muted' style={{ fontSize: '15px' }}>{t('academic.subtitle')}</p>
                </Col>
            </Row>

            <Row className='mt-3'>
                <Col>
                    <Card className='border-0 rounded-4 shadow-sm'>
                        <Card.Body className='p-4'>
                            <div className='d-flex align-items-center gap-2 mb-4'>
                                <BookIcon />
                                <h5 className='fw-bold text-black mb-0'>Información del Curso</h5>
                            </div>
                            <Row className='g-4 mb-4'>
                                <Col xs={6} md={3}>
                                    <p className='mb-1 small' style={{ color: '#9ca3af' }}>{t('academic.course')}</p>
                                    <p className='fw-bold mb-0' style={{ fontSize: '15px', color: '#111827' }}>{enrollment?.courseName || '—'}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p className='mb-1 small' style={{ color: '#9ca3af' }}>{t('academic.group')}</p>
                                    <p className='fw-bold mb-0' style={{ fontSize: '15px', color: '#111827' }}>{enrollment?.groupName || '—'}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p className='mb-1 small' style={{ color: '#9ca3af' }}>{t('academic.modality')}</p>
                                    <p className='fw-bold mb-0' style={{ fontSize: '15px', color: '#111827' }}>{enrollment?.modality || '—'}</p>
                                </Col>
                                <Col xs={6} md={3}>
                                    <p className='mb-1 small' style={{ color: '#9ca3af' }}>{t('academic.campus')}</p>
                                    <p className='fw-bold mb-0' style={{ fontSize: '15px', color: '#111827' }}>{enrollment?.sessions?.[0]?.campusName || '—'}</p>
                                </Col>
                            </Row>
                            <div>
                                <p className='mb-1 small' style={{ color: '#9ca3af' }}>{t('academic.tutor')}</p>
                                <div className='d-flex align-items-center gap-2'>
                                    <UserIcon />
                                    <p className='fw-bold mb-0' style={{ fontSize: '15px', color: '#111827' }}>{enrollment?.teacher || '—'}</p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='mt-4'>
                <Col>
                    <div className='d-flex gap-2 mb-2 invoice-filters'>
                        {[
                            { key: 'schedule', label: t('academic.schedule'), icon: <CalendarSmallIcon /> },
                            { key: 'nextclasses', label: t('academic.nextclasses'), icon: <ClockIcon /> },
                            { key: 'resources', label: t('academic.resources'), icon: <BookIcon /> },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`rounded-pill invoice-filter-btn d-inline-flex align-items-center gap-1 border-0 px-3 py-1 ${activeTab === tab.key ? 'active' : ''}`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </Col>
            </Row>

            {activeTab === 'schedule' && (
                <Row className='mt-2'>
                    <Col>
                        <Card className='border-0 rounded-4 shadow-sm'>
                            <Card.Body className='p-4'>
                                <h5 className='fw-bold text-black mb-1'>Horario Semanal</h5>
                                <p className='text-muted mb-4' style={{ fontSize: '14px' }}>Tu horario basado en el cuadrante de planificación</p>

                                {weekDays.map((day, idx) => (
                                    <div key={idx} className='mb-4'>
                                        <div className='d-flex align-items-center gap-2 mb-3'>
                                            <CalendarDayIcon />
                                            <h6 className='fw-bold text-black mb-0' style={{ fontSize: '16px' }}>{day.label}</h6>
                                            {day.isToday && (
                                                <Badge bg="danger" className='rounded-pill px-3' style={{ fontSize: '12px' }}>Hoy</Badge>
                                            )}
                                        </div>
                                        {day.sessions.map((ses, cIdx) => {
                                            const start = toLocal(ses.startDatetime)
                                            const end = toLocal(ses.endDatetime)
                                            const time = `${formatTime(start)} - ${formatTime(end)}`
                                            const isOnline = ses.modality && ses.modality.toLowerCase().includes('online')
                                            return (
                                                <Card
                                                    key={cIdx}
                                                    className={`border rounded-3 mb-2 ${day.isToday && isOnline ? 'border-danger bg-danger bg-opacity-10' : 'border-light'}`}
                                                >
                                                    <Card.Body className='p-3'>
                                                        <div className='d-flex justify-content-between align-items-start'>
                                                            <div className='d-flex gap-3' style={{ flex: 1 }}>
                                                                <div
                                                                    className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                                                                    style={{ width: '40px', height: '40px', backgroundColor: isOnline ? '#fee2e2' : '#f3f4f6' }}
                                                                >
                                                                    {isOnline
                                                                        ? <VideoIcon />
                                                                        : <MapPinIcon />
                                                                    }
                                                                </div>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div className='d-flex align-items-center gap-2 mb-1'>
                                                                        <h6 className='fw-bold text-black mb-0'>{ses.name || ses.courseName}</h6>
                                                                    </div>
                                                                    <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{ses.teacher}</p>
                                                                    <div className='d-flex align-items-center gap-3 mt-1'>
                                                                        <span className='d-flex align-items-center gap-1 text-muted' style={{ fontSize: '13px' }}>
                                                                            <ClockIcon /> {time}
                                                                        </span>
                                                                        {ses.classroom && (
                                                                            <span className='d-flex align-items-center gap-1 text-muted' style={{ fontSize: '13px' }}>
                                                                                <MapPinIcon /> {ses.classroom}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Badge
                                                                bg={isOnline ? 'danger' : 'primary'}
                                                                className='rounded-pill flex-shrink-0 ms-2'
                                                                style={{ padding: '4px 14px', fontWeight: 500, fontSize: '12px' }}
                                                            >
                                                                {isOnline ? t('academic.online') : t('academic.inperson')}
                                                            </Badge>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            )
                                        })}
                                        {day.sessions.length === 0 && (
                                            <p className='text-muted small ms-1 mb-0'>Sin clases</p>
                                        )}
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'nextclasses' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 shadow-sm p-4'>
                            {allSessions.length > 0
                                ? allSessions
                                    .filter(s => toLocal(s.startDatetime) > now)
                                    .sort((a, b) => toLocal(a.startDatetime) - toLocal(b.startDatetime))
                                    .slice(0, 10)
                                    .map((ses, i) => {
                                        const start = toLocal(ses.startDatetime)
                                        const isOnline = ses.modality?.toLowerCase().includes('online')
                                        return (
                                            <div key={i} className='d-flex align-items-start border border-light rounded-3 p-3 mb-2'>
                                                <div className='d-flex gap-3' style={{ flex: 1 }}>
                                                    <div
                                                        className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                                                        style={{ width: '40px', height: '40px', backgroundColor: isOnline ? '#fee2e2' : '#f3f4f6' }}
                                                    >
                                                        {isOnline ? <VideoIcon /> : <MapPinIcon />}
                                                    </div>
                                                    <div>
                                                        <p className='fw-semibold mb-0 text-black'>{ses.name || ses.courseName}</p>
                                                        <p className='text-muted small mb-0'>
                                                            {start.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} · {formatTime(start)}
                                                        </p>
                                                        <p className='text-muted small mb-0 d-flex align-items-center gap-1'>
                                                            <UserIcon /> {ses.teacher} · {ses.classroom || '—'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Badge bg={isOnline ? 'danger' : 'primary'} className='rounded-pill'>{isOnline ? 'Online' : 'Presencial'}</Badge>
                                            </div>
                                        )
                                    })
                                : <p className='text-muted text-center py-5 mb-0'>{t('academic.nextclasses')}</p>
                            }
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'resources' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 shadow-sm p-4'>
                            <p className='text-muted text-center py-5 mb-0'>{t('academic.resources')}</p>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    )
}
